import { useEffect, useRef, useState } from "react";
import { getUSPhoneDigits, formatUSPhone } from "../utils/phoneFormatter";
import axios from "axios";
import { baseURL } from "../config/api";
import { Helmet } from "react-helmet-async";
import CustomFormatDatePicker from "./CustomFormatDatePicker";
import InputPicker from "./InputPicker";
import { combineDateAndTime } from "../utils/getUserDateFormat";

interface UploadedFile {
    fileName: string;
    type: string;
}

export const GetQuote = () => {
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [respondVia, setRespondVia] = useState("");
    const [departureDate, setDepartureDate] = useState("");
    const [departureTime, setDepartureTime] = useState("");
    const [returnDate, setReturnDate] = useState("");
    const [returnTime, setReturnTime] = useState("");

    const [departureCity, setDepartureCity] = useState("");
    const [departureState, setDepartureState] = useState("");

    const [destinationCity, setDestinationCity] = useState("");
    const [destinationState, setDestinationState] = useState("");
    const [tripType, setTripType] = useState("");

    const [groupTypes, setGroupTypes] = useState<any[]>([]);
    const [groupTypeId, setGroupTypeId] = useState<number | "">("");

    const [passengers, setPassengers] = useState("");

    const [additionalInfo, setAdditionalInfo] = useState("");

    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isPhoneValid, setIsPhoneValid] = useState(true);

    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [previewIndex, setPreviewIndex] =
        useState<number | null>(null);

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const departureTimeRef = useRef<HTMLInputElement>(null);
    const returnTimeRef = useRef<HTMLInputElement>(null);

    const previewFile =
        previewIndex !== null
            ? uploadedFiles[previewIndex]
            : null;

    useEffect(() => {
        axios
            .get(`${baseURL}group-types/`)
            .then((response) => {
                setGroupTypes(response.data.data || []);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    const handlePhoneChangeLocal = (value: string) => {
        const input = document.activeElement as HTMLInputElement;

        if (!input || input.tagName !== "INPUT") {
            setPhone(getUSPhoneDigits(value));
            return;
        }

        const cursor = input.selectionStart || 0;

        const digitsBeforeCursor = value
            .slice(0, cursor)
            .replace(/\D/g, "").length;

        const digits = getUSPhoneDigits(value);

        setPhone(digits);

        requestAnimationFrame(() => {
            const formatted = formatUSPhone(digits);

            let newCursor = formatted.length;
            let digitCount = 0;

            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) digitCount++;

                if (digitCount >= digitsBeforeCursor) {
                    newCursor = i + 1;
                    break;
                }
            }

            try {
                input.setSelectionRange(newCursor, newCursor);
            } catch (error) {
                console.error("Failed to set cursor position:", error);
            }
        });
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (email.trim() !== "") {
            timer = setTimeout(() => {
                const valid =
                    /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email.trim());

                setIsEmailValid(valid);
            }, 800);
        } else {
            setIsEmailValid(true);
        }

        return () => clearTimeout(timer);
    }, [email]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (phone.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(phone);
                setIsPhoneValid(valid);
            }, 500);
        } else {
            setIsPhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [phone]);

    const handleUpload = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = Array.from(e.target.files || []);
        setUploading(true);
        setUploadProgress(0);
        for (const file of files) {
            const formData = new FormData();
            formData.append("file", file);

            const res = await axios.post(
                `${baseURL}common-files/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },

                    onUploadProgress: (progressEvent) => {
                        const percent = Math.round(
                            (progressEvent.loaded * 100) /
                            (progressEvent.total || 1)
                        );

                        setUploadProgress(percent);
                    },
                }
            );

            setUploadedFiles((prev) => [
                ...prev,
                {
                    fileName: res?.data?.data?.name,
                    type: file.type,
                },
            ]);
        }
        setUploading(false);
        setUploadProgress(100);

        setTimeout(() => {
            setUploadProgress(0);
        }, 500);
        e.target.value = "";
    };

    const requiresReturnDate = tripType === "Round Trip";

    const isFormValid =
        firstName.trim() !== "" &&
        lastName.trim() !== "" &&
        email.trim() !== "" &&
        phone.trim() !== "" &&
        respondVia.trim() !== "" &&
        departureDate !== "" &&
        departureTime.trim() !== "" &&
        departureCity.trim() !== "" &&
        departureState.trim() !== "" &&
        destinationCity.trim() !== "" &&
        destinationState.trim() !== "" &&
        tripType.trim() !== "" &&
        passengers !== "" &&
        (!requiresReturnDate || (returnDate.trim() !== "" && returnTime.trim() !== "")) &&
        isEmailValid &&
        isPhoneValid &&
        !loading;

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setCompanyName("");
        setEmail("");
        setPhone("");
        setRespondVia("");
        setDepartureDate("");
        setDepartureTime("");
        setReturnDate("");
        setReturnTime("");
        setDepartureCity("");
        setDepartureState("");
        setDestinationCity("");
        setDestinationState("");
        setTripType("");
        setGroupTypeId("");
        setPassengers("");
        setAdditionalInfo("");
        setUploadedFiles([]);
        setPreviewIndex(null);
        setUploading(false);
        setUploadProgress(0);
        setIsEmailValid(true);
        setIsPhoneValid(true);
    };

    const handleDelete = (index: number) => {
        setUploadedFiles((prev) =>
            prev.filter((_, i) => i !== index)
        );

        if (previewFile?.fileName === uploadedFiles[index]?.fileName) {
            setPreviewIndex(null);
        }
    };

    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        if (!isFormValid) return;

        setLoading(true);

        try {
            const quoteResponse = await axios.post(
                `${baseURL}quotes/`,
                {
                    firstName,
                    lastName,
                    email,
                    phone,
                    company: companyName,
                    respondVia,
                    departureDate: combineDateAndTime(
                        departureDate,
                        departureTime
                    ),
                    returnDate: returnDate
                        ? combineDateAndTime(
                            returnDate,
                            returnTime
                        )
                        : null,
                    departureCity,
                    departureState,
                    destinationCity,
                    destinationState,
                    tripType,
                    groupTypeId: groupTypeId || 0,
                    statusId: 1,
                    numberOfPassengers: Number(passengers),
                    additionalTripInformation: additionalInfo,
                    createdBy: "Website"
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            const quoteId = quoteResponse.data.data.id;

            if (uploadedFiles.length > 0) {
                await axios.post(
                    `${baseURL}file-upload/bulk`,
                    {
                        files: uploadedFiles.map((file) => ({
                            attributeId: quoteId,
                            attributeName: "Quote",
                            fileName: file.fileName,
                            uploadedBy: `${firstName} ${lastName}`.trim(),
                        })),
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
            }

            resetForm();
            setSubmitted(true);

            window.scrollTo({
                top: 250,
                behavior: "smooth",
            });

            setTimeout(() => {
                setSubmitted(false);
            }, 5000);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="bg-primary">
            <Helmet>
                <title>Quote Velox Charter | Request a Transportation Quote</title>

                <meta
                    name="description"
                    content="Quote Velox Charter for professional charter transportation services. Request a free quote for airport transfers, corporate transportation, group travel, employee shuttles, event transportation, and private charter services."
                />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4">
                <div className="h-[260px] md:h-[320px] flex flex-col items-center justify-center text-center">
                    <h1 className="mt-5 text-white text-4xl md:text-6xl font-bold">
                        Request Your Quote
                    </h1>

                    <p className="mt-6 max-w-3xl text-lg text-white/90 leading-8">
                        Whether you're planning an airport transfer, corporate event,
                        wedding, school trip, or private charter, we'll provide a
                        personalized transportation solution tailored to your group's needs.
                    </p>

                </div>
            </div>

            <section className="bg-[#f5f5f5] py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-8">

                        <div>
                            <div className="mb-8 rounded-xl border border-blue-100 bg-blue-50 p-6">
                                <h3 className="text-xl font-semibold text-primary">
                                    Need a Quote Quickly?
                                </h3>

                                <p className="mt-2 text-gray-600">
                                    Speak directly with one of our transportation specialists for immediate assistance.
                                </p>

                                <a
                                    href="tel:2817203007"
                                    className="mt-5 inline-flex items-center gap-3 rounded-lg bg-primary px-6 py-3 text-white font-semibold whitespace-nowrap hover:bg-[#004b86] transition"
                                >
                                    <span className="material-symbols-outlined">
                                        call
                                    </span>

                                    (281) 720-3007
                                </a>
                            </div>

                            <p className="mb-8 text-gray-600">
                                Complete the form below and we'll prepare a personalized transportation quote based on your travel requirements.
                            </p>

                            <form onSubmit={handleSubmit}>
                                {submitted && (
                                    <div className="mb-4 p-4 rounded-md bg-green-100 text-green-700">
                                        Thank you! We'll be in touch soon.
                                    </div>
                                )}
                                <div className="mb-10">
                                    <h2 className="text-2xl font-semibold text-primary mb-6">
                                        Contact Information
                                    </h2>

                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                First Name <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                placeholder="Enter first name"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                placeholder="Enter last name"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Enter email address"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />

                                            {!isEmailValid && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    Please enter a valid email address
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Phone Number <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={formatUSPhone(phone)}
                                                onChange={(e) =>
                                                    handlePhoneChangeLocal(e.target.value)
                                                }
                                                placeholder="(281) 371-9009"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />

                                            {!isPhoneValid && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    Please enter a valid 10-digit phone number
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Name
                                            </label>

                                            <input
                                                type="text"
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                placeholder="Enter company name (optional)"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Respond Via <span className="text-red-500">*</span>
                                            </label>

                                            <select
                                                value={respondVia}
                                                onChange={(e) => setRespondVia(e.target.value)}
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            >
                                                <option value="">Select Contact Method</option>
                                                <option value="Phone">Phone</option>
                                                <option value="Email">Email</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-10">
                                    <h2 className="text-2xl font-semibold text-primary mb-6">
                                        Trip Details
                                    </h2>

                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Trip Type <span className="text-red-500">*</span>
                                            </label>

                                            <div className="flex flex-wrap gap-2 h-12 items-center">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="Round Trip"
                                                        checked={tripType === "Round Trip"}
                                                        onChange={(e) => setTripType(e.target.value)}
                                                    />
                                                    <span>Round Trip</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="One Way"
                                                        checked={tripType === "One Way"}
                                                        onChange={(e) => {
                                                            setTripType(e.target.value);
                                                            setReturnDate("");
                                                        }}
                                                    />
                                                    <span>One Way</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="Sameday Return"
                                                        checked={tripType === "Sameday Return"}
                                                        onChange={(e) => setTripType(e.target.value)}
                                                    />
                                                    <span>Same Day Return</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="Overnight"
                                                        checked={tripType === "Overnight"}
                                                        onChange={(e) => setTripType(e.target.value)}
                                                    />
                                                    <span>Overnight</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Departure Date <span className="text-red-500">*</span>
                                            </label>

                                            <div className="flex flex-col gap-2 md:flex-row">
                                                <div className="relative w-full md:w-1/2">
                                                    <CustomFormatDatePicker
                                                        maxDate="9999-12-31T23:59"
                                                        value={departureDate}
                                                        onChange={(value) => {
                                                            setDepartureDate(value);
                                                        }}
                                                        required={true}
                                                    />
                                                </div>

                                                <div className="w-full md:w-1/2">
                                                    <InputPicker
                                                        type="time"
                                                        inputRef={departureTimeRef}
                                                        inputValue={departureTime}
                                                        setInputValue={(inputValue) => setDepartureTime(inputValue)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {tripType !== "One Way" && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Return Date{" "}
                                                    {requiresReturnDate && (
                                                        <span className="text-red-500">*</span>
                                                    )}
                                                </label>

                                                <div className="flex flex-col gap-2 md:flex-row">
                                                    <div className="relative w-full md:w-1/2">
                                                        <CustomFormatDatePicker
                                                            maxDate="9999-12-31T23:59"
                                                            value={returnDate}
                                                            onChange={(value) => {
                                                                setReturnDate(value);
                                                            }}
                                                            required={requiresReturnDate}
                                                        />
                                                    </div>

                                                    <div className="w-full md:w-1/2">
                                                        <InputPicker
                                                            type="time"
                                                            inputRef={returnTimeRef}
                                                            inputValue={returnTime}
                                                            setInputValue={(inputValue) => setReturnTime(inputValue)}
                                                            required={requiresReturnDate}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Departure City <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={departureCity}
                                                onChange={(e) => setDepartureCity(e.target.value)}
                                                placeholder="Enter departure city"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Departure State <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={departureState}
                                                onChange={(e) => setDepartureState(e.target.value)}
                                                placeholder="Enter departure state"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Destination City <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={destinationCity}
                                                onChange={(e) => setDestinationCity(e.target.value)}
                                                placeholder="Enter destination city"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Destination State <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={destinationState}
                                                onChange={(e) => setDestinationState(e.target.value)}
                                                placeholder="Enter destination state"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Upload Itinerary
                                            </label>

                                            <input
                                                id="itinerary-upload"
                                                type="file"
                                                multiple
                                                hidden
                                                onChange={handleUpload}
                                            />

                                            <label
                                                htmlFor="itinerary-upload"
                                                className="inline-block px-3 py-1 border border-gray-400 rounded cursor-pointer bg-[#f3f3f3] hover:bg-[#e6e6e6] text-sm"
                                            >
                                                Choose Files
                                            </label>

                                            {uploading && (
                                                <div className="mt-4">

                                                    <div className="w-full h-2 bg-gray-200 rounded-full">

                                                        <div
                                                            className="h-full bg-primary rounded-full transition-all"
                                                            style={{
                                                                width: `${uploadProgress}%`,
                                                            }}
                                                        />

                                                    </div>

                                                    <p className="text-sm text-gray-600 mt-2">
                                                        Uploading... {uploadProgress}%
                                                    </p>

                                                </div>
                                            )}

                                            {uploadedFiles.length > 0 && (
                                                <div className="mt-4 flex flex-wrap gap-3">
                                                    {uploadedFiles.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-300 shadow-sm hover:shadow-md transition cursor-pointer"
                                                        >
                                                            {file.type.startsWith("image") ? (
                                                                <img
                                                                    alt="Uploading itinerary"
                                                                    src={`${baseURL}common-files/get/${file.fileName}`}
                                                                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                                                                    onClick={() => setPreviewIndex(index)}
                                                                />
                                                            ) : (
                                                                <div
                                                                    className="flex h-full items-center justify-center text-3xl gap-2"
                                                                    onClick={() => setPreviewIndex(index)}
                                                                >
                                                                    📄
                                                                </div>
                                                            )}

                                                            <button
                                                                className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white shadow-lg hover:bg-red-600 transition"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(index);
                                                                }}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-10">
                                    <h2 className="text-2xl font-semibold text-primary mb-6">
                                        Group Details
                                    </h2>

                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Group Type
                                            </label>

                                            <select
                                                value={groupTypeId}
                                                onChange={(e) => setGroupTypeId(Number(e.target.value))}
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            >
                                                <option value="">Select Group Type</option>

                                                {groupTypes.map((item: any) => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Number of Passengers <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="number"
                                                min={1}
                                                value={passengers}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    if (/^\d*$/.test(value)) {
                                                        setPassengers(value);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (
                                                        ["-", "+", ".", "e", "E"].includes(e.key)
                                                    ) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const pastedText = e.clipboardData.getData("text");

                                                    if (!/^\d+$/.test(pastedText)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                placeholder="Enter number of passengers"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        </div>

                                    </div>

                                    <div className="mt-5">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Additional Trip Information
                                        </label>

                                        <textarea
                                            rows={6}
                                            value={additionalInfo}
                                            onChange={(e) => setAdditionalInfo(e.target.value)}
                                            placeholder="Provide any additional information about your trip..."
                                            className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={!isFormValid}
                                    className=" w-full mt-5 h-12 bg-primary text-white font-medium rounded-md hover:bg-[#004b86] transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Sending..." : "Request Free Quote"}
                                </button>
                            </form>

                            <section className="mt-20">
                                <div className="grid md:grid-cols-4 gap-6">

                                    <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                        <div className="text-4xl mb-3">🛡️</div>
                                        <h3 className="font-semibold">Licensed & Insured</h3>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Safety and reliability are our top priorities.
                                        </p>
                                    </div>

                                    <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                        <div className="text-4xl mb-3">🚌</div>
                                        <h3 className="font-semibold">Modern Fleet</h3>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Comfortable vehicles for groups of all sizes.
                                        </p>
                                    </div>

                                    <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                        <div className="text-4xl mb-3">👨‍✈️</div>
                                        <h3 className="font-semibold">Professional Drivers</h3>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Experienced drivers dedicated to exceptional service.
                                        </p>
                                    </div>

                                    <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                        <div className="text-4xl mb-3">💲</div>
                                        <h3 className="font-semibold">Competitive Pricing</h3>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Transparent pricing with no hidden fees.
                                        </p>
                                    </div>

                                </div>
                            </section>
                        </div>

                        <div className="lg:sticky lg:top-8 self-start">
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">

                                <div className="bg-primary px-6 py-5">
                                    <h3 className="text-xl font-semibold text-white">
                                        Need Assistance?
                                    </h3>

                                    <p className="text-white/80 text-sm mt-1">
                                        Our transportation specialists are here to help.
                                    </p>
                                </div>

                                <div className="p-6 space-y-6">
                                    <a
                                        href="tel:2817203007"
                                        className="flex items-center gap-4 group"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-primary transition">
                                            <span className="material-symbols-outlined text-primary group-hover:text-white">
                                                call
                                            </span>
                                        </div>

                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Call Us
                                            </p>

                                            <p className="font-semibold text-lg text-gray-900">
                                                (281) 720-3007
                                            </p>
                                        </div>
                                    </a>

                                    <hr />

                                    <a
                                        href="mailto:info@veloxcharter.com"
                                        className="flex items-center gap-4 group"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-primary transition">
                                            <span className="material-symbols-outlined text-primary group-hover:text-white">
                                                mail
                                            </span>
                                        </div>

                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Email Us
                                            </p>

                                            <p className="font-medium text-gray-900 break-all">
                                                info@veloxcharter.com
                                            </p>
                                        </div>
                                    </a>

                                    <hr />

                                    <div className="flex gap-4">
                                        <span className="material-symbols-outlined text-primary">
                                            schedule
                                        </span>

                                        <div>
                                            <h4 className="font-semibold">
                                                Fast Response
                                            </h4>

                                            <p className="text-sm text-gray-600 mt-1">
                                                Most quote requests receive a response within one
                                                business day.
                                            </p>
                                        </div>
                                    </div>

                                    <hr />

                                    <div>
                                        <h4 className="font-semibold mb-3">
                                            Why Choose Velox Charter?
                                        </h4>

                                        <ul className="space-y-2 text-sm text-gray-600">
                                            <li>✓ Professional Drivers</li>
                                            <li>✓ Modern Fleet</li>
                                            <li>✓ Competitive Pricing</li>
                                            <li>✓ Airport & Event Transportation</li>
                                            <li>✓ Reliable Group Travel</li>
                                        </ul>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </section>
    );
};

export default GetQuote;